import { useEffect, useState } from 'react'

const CONSENT_KEY = 'cookie_consent'
const CONSENT_EXPIRY_MS = 180 * 24 * 60 * 60 * 1000 // 180 days

// Handles both legacy plain string format ('accepted') and new JSON format
function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    if (raw === 'accepted' || raw === 'rejected') return raw // legacy
    const data = JSON.parse(raw)
    if (Date.now() - (data.timestamp || 0) > CONSENT_EXPIRY_MS) {
      localStorage.removeItem(CONSENT_KEY)
      return null
    }
    return data.value
  } catch {
    return null
  }
}

function saveConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ value, timestamp: Date.now() }))
  } catch {}
}

function loadHotjar() {
  if (window._hjSettings) return
  window.hj = window.hj || function () { (window.hj.q = window.hj.q || []).push(arguments) }
  window._hjSettings = { hjid: 6611644, hjsv: 6 }
  const s = document.createElement('script')
  s.async = true
  s.src = 'https://static.hotjar.com/c/hotjar-6611644.js?sv=6'
  document.head.appendChild(s)
}

function loadDataFa() {
  if (document.querySelector('script[data-website-id="dfid_9jGKsqu0dRKFEbSClzW5w"]')) return
  const s = document.createElement('script')
  s.defer = true
  s.setAttribute('data-website-id', 'dfid_9jGKsqu0dRKFEbSClzW5w')
  s.setAttribute('data-domain', 'console.notifyer-systems.com')
  s.setAttribute('data-allowed-hostnames', 'WhatsAble.app')
  s.src = 'https://datafa.st/js/script.js'
  document.head.appendChild(s)
}

function applyConsent(value) {
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }

  if (value === 'accepted') {
    window.gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    })
    if (typeof window.fbq !== 'undefined') window.fbq('consent', 'grant')
    loadHotjar()
    loadDataFa()
  } else {
    if (typeof window.fbq !== 'undefined') window.fbq('consent', 'revoke')
  }
}

export default function CookieConsent({ onVisibilityChange }) {
  const [visible, setVisible] = useState(false)
  const [hasConsented, setHasConsented] = useState(false)

  useEffect(() => {
    const stored = getStoredConsent()
    if (stored) {
      applyConsent(stored)
      setHasConsented(true)
      return
    }
    setVisible(true)
  }, [])

  useEffect(() => {
    onVisibilityChange?.(visible)
  }, [visible, onVisibilityChange])

  function handleAccept() {
    saveConsent('accepted')
    applyConsent('accepted')
    setVisible(false)
    setHasConsented(true)
  }

  function handleReject() {
    saveConsent('rejected')
    applyConsent('rejected')
    setVisible(false)
    setHasConsented(true)
  }

  function handleReopen() {
    try { localStorage.removeItem(CONSENT_KEY) } catch {}
    setHasConsented(false)
    setVisible(true)
  }

  return (
    <>
      {/* Persistent cookie settings button — visible after user has made a choice */}
      {hasConsented && !visible && (
        <button
          onClick={handleReopen}
          className="fixed bottom-4 left-4 z-40 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-gray-200 bg-white/90 backdrop-blur-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors shadow-sm"
          aria-label="Open cookie settings"
        >
          <span aria-hidden="true">🍪</span> Cookie settings
        </button>
      )}

      {/* Consent banner */}
      {visible && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          className="fixed bottom-0 left-0 right-0 z-40"
        >
          <div className="bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]">
            <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <p className="flex-1 text-xs text-gray-500 leading-relaxed">
                We use cookies and third-party tools — including{' '}
                <span className="text-gray-700 font-medium">Google Analytics, Google Ads, Meta Pixel</span> and{' '}
                <span className="text-gray-700 font-medium">Hotjar</span> — to measure performance and improve your
                experience. Essential cookies are always active. See our{' '}
                <a
                  href="/privacy-policy"
                  className="text-gray-700 underline underline-offset-2 hover:text-gray-900 transition-colors"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleReject}
                  className="text-xs px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap"
                >
                  Essential only
                </button>
                <button
                  onClick={handleAccept}
                  className="text-xs px-4 py-2 rounded-md font-semibold text-white transition-all whitespace-nowrap shadow-sm hover:brightness-110"
                  style={{ backgroundColor: '#00D876' }}
                >
                  Accept all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
